import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getEmployerDossier } from "../../api/dossier/getEmployerDossier";
import { getEmployerDossierTermination } from "../../api/dossier/getEmployerDossierTermination";
import { getEmployerDossierTerminationStatus } from "../../api/dossier/getEmployerDossierTerminationStatus";
import { setDriverNotifiedOfTermination } from "../../api/dossier/setDriverNotifiedOfTermination";
import AutoPilotAlert from "../../components/Alert";
import APModal from "../../components/APModal";
import APButton from "../../components/buttons/APButton";
import { APButtonLink } from "../../components/buttons/APButtonLink";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import InfoButton from "../../components/buttons/InfoButton";
import FaqList from "../../components/faq/FaqList";
import PageContainer from "../../components/PageContainer";
import DossierStatusStepper from "../../dossier/components/DossierStatusStepper";
import { formatDate } from "../../helpers/dates";
import {
  filterAccountsByPermission,
  hasPermissionForAccount,
} from "../../helpers/user";
import { useFaqs } from "../../hooks/useFaqs";
import { useApi } from "../../providers/ApiProvider";
import { useAuth } from "../../providers/AuthProvider";
import { useConfirm } from "../../providers/ConfirmProvider";
import { User } from "../../types/User";
import DossierSummary from "../components/DossierSummary";
import DossierTerminationSummary from "../components/DossierTerminationSummary";

const EmployerViewDossierTermination = ({ me }: { me: User }) => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const { fetchFile } = useApi();
  const { getScopedPageUrl } = useAuth();
  const [showNoteToEmployerModal, setShowNoteToEmployerModal] = useState(false);
  const confirm = useConfirm();

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const authorisedAccounts = me.accountPermissions
    ? filterAccountsByPermission(
        me.accountPermissions,
        "viewDossierTerminations"
      )
    : [];

  const dossier = getEmployerDossier(dossierId);
  const dossierTermination = getEmployerDossierTermination(dossierId, {
    disabled: !navigationCheckComplete,
  });
  const terminationStatus = getEmployerDossierTerminationStatus(
    dossierId,
    "Closing",
    {
      disabled: !navigationCheckComplete,
    }
  );

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "employer-lease-closure",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  const { mutateAsync: mutateDriverNotifiedOfTerminationAsync } =
    setDriverNotifiedOfTermination(dossierId, {
      mutationOptions: {
        successAlertMessage:
          "Employee will be contacted to progress the termination.",
      },
    });

  if (dossier.isError) {
    throw dossier.error;
  }

  if (dossierTermination.isError) {
    throw dossierTermination.error;
  }

  if (terminationStatus.isError) {
    throw terminationStatus.error;
  }

  const isLoading =
    dossier.isLoading ||
    dossierTermination.isLoading ||
    terminationStatus.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    dossier.isSuccess &&
    dossierTermination.isSuccess &&
    terminationStatus.isSuccess &&
    isSuccessFaqs;

  useEffect(() => {
    if (dossier.isSuccess && !dossier.isFetching) {
      if (
        authorisedAccounts.length > 0 &&
        dossier.data?.opportunity &&
        dossier.data?.terminationId &&
        ["Closing", "Closed"].includes(dossier.data.status) &&
        hasPermissionForAccount(
          me.accountPermissions,
          "viewDossierTerminations",
          dossier.data.opportunity.parentAccountId ?? ""
        )
      ) {
        setNavigationCheck(true);
      } else {
        navigate(getScopedPageUrl("employerHome"), { replace: true });
      }
    }
  }, [dossier.isSuccess, dossier.isFetching]);

  const authorisedToTerminate = useMemo(() => {
    return (
      dossier.data?.opportunity?.parentAccountId &&
      hasPermissionForAccount(
        me.accountPermissions,
        "terminateDossiers",
        dossier.data.opportunity.parentAccountId
      )
    );
  }, [dossier.data]);

  const downloadPayrollAdvicePdf = async (
    dossierId: string,
    fileName: string
  ) => {
    fetchFile(
      "getEmployerDossierTerminationPayrollAdvicePDF",
      dossierId,
      fileName
    );
  };

  const downloadInvoicePdf = async (dossierId: string, fileName: string) => {
    fetchFile("getEmployerDossierTerminationInvoicePDF", dossierId, fileName);
  };

  const onStatusItemClick = () => {
    setShowNoteToEmployerModal(true);
  };

  const onCloseNoteToEmployerModal = () => {
    setShowNoteToEmployerModal(false);
  };

  const onDriverNotifiedOfTerminationClick = async () => {
    const ok = await confirm({
      title: "Employee Notified of Termination?",
      description:
        "The employee will be contacted to progress the termination.",
    });

    if (ok) {
      await mutateDriverNotifiedOfTerminationAsync();
    }
  };

  const renderAppLocation = faqProps.renderAppLocation;

  let pageTitle = "";

  if (dossier.data?.status === "Closing") {
    pageTitle = "Terminating";
  } else if (dossier.data?.status === "Closed") {
    pageTitle = "Terminated";
  }

  return (
    <PageContainer
      title={pageTitle}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "EmployerClose-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
      loading={isLoading || !navigationCheckComplete}
    >
      {isSuccess && navigationCheckComplete && (
        <Stack spacing={2}>
          <DossierSummary
            dossier={dossier.data}
            displayedFields={[
              "employee",
              "employer",
              "contractId",
              "startDate",
              "vehicle",
            ]}
          />
          <DossierTerminationSummary
            dossierTermination={{
              leavingEmployer: dossierTermination.data.leavingEmployer,
              lastDateOfEmployment:
                dossierTermination.data.lastDateOfEmployment,
              targetLastPayrollDate:
                dossierTermination.data.targetLastPayrollDate,
              endDate: dossierTermination.data.terminationDateSet
                ? dossierTermination.data.endDate
                : null,
              driverNotifiedOfTermination:
                dossierTermination.data.driverNotifiedOfTermination,
              terminationReason: dossierTermination.data.terminationReason,
              requestedByContactName:
                dossierTermination.data.requestedByContactName,
              requestedAt: dossierTermination.data.requestedAt,
            }}
            displayedFields={[
              "leavingEmployer",
              "lastDateOfEmployment",
              "targetLastPayrollDate",
              "endDate",
              "terminationReason",
              "driverNotifiedOfTermination",
              "requestedByContactName",
              "requestedAt",
            ]}
            labelOverrides={{
              endDate: "Novation End",
            }}
            appLocations={{
              endDate: "EmployerClose-EndDate",
            }}
            fieldActions={{
              driverNotifiedOfTermination:
                dossierTermination.data.driverNotifiedOfTermination === false &&
                authorisedToTerminate ? (
                  <Box>
                    <APButton
                      text="Employee Notified"
                      icon="play"
                      variant="contained"
                      color="secondary"
                      onClick={onDriverNotifiedOfTerminationClick}
                    />
                  </Box>
                ) : undefined,
            }}
            renderAppLocation={renderAppLocation}
          />

          <Stack spacing={1}>
            {dossierTermination.data.hasTerminationInvoice && (
              <APButtonLink
                text={`Termination Invoice${
                  dossierTermination.data.terminationInvoiceId
                    ? ` ${dossierTermination.data.terminationInvoiceId} `
                    : ""
                }${
                  dossierTermination.data.terminationInvoiceSentDate
                    ? ` Sent: ${formatDate(
                        dossierTermination.data.terminationInvoiceSentDate
                      )}`
                    : ""
                }`}
                onClick={() =>
                  downloadInvoicePdf(
                    dossierId,
                    dossierTermination.data.terminationInvoiceName ?? ""
                  )
                }
                size="medium"
              />
            )}

            {dossierTermination.data.hasTerminationPayrollAdvice && (
              <APButtonLink
                text={`Termination Payroll Advice${
                  dossierTermination.data.terminationPayrollAdviceSentDate
                    ? ` Sent: ${formatDate(
                        dossierTermination.data.terminationPayrollAdviceSentDate
                      )}`
                    : ""
                }`}
                onClick={() =>
                  downloadPayrollAdvicePdf(
                    dossierId,
                    dossierTermination.data.terminationPayrollAdviceName ?? ""
                  )
                }
                size="medium"
              />
            )}
          </Stack>

          <Box sx={{ pt: 1, pb: 2 }}>
            <Box ml={1}>
              <DossierStatusStepper
                dossierStatus={terminationStatus.data}
                onItemClick={
                  dossierTermination.data.noteToEmployer
                    ? onStatusItemClick
                    : undefined
                }
              />
            </Box>
            <APModal
              isOpen={showNoteToEmployerModal}
              onClose={onCloseNoteToEmployerModal}
            >
              <Box>
                <AutoPilotAlert
                  severity="info"
                  message={dossierTermination.data.noteToEmployer ?? ""}
                  asHTML={true}
                />
              </Box>
            </APModal>
          </Box>

          {dossierTermination.data.noteToEmployer && (
            <Box>
              <AutoPilotAlert
                severity="info"
                message={dossierTermination.data.noteToEmployer}
                asHTML={true}
              />
            </Box>
          )}

          <FaqList
            pageId="employer-lease-closure"
            appLocationIdentifier="EmployerClose-FAQList"
            faqContextIds={{
              opportunityId: dossier.data.opportunity?.id ?? undefined,
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                pt: 2,
              }}
            >
              <Box sx={{ display: "flex", columnGap: 1, alignItems: "center" }}>
                <BackButton />
                <HomeButton />
              </Box>
              <APContactHelpButton
                regardingOpportunityId={
                  dossier.data.opportunity?.id ?? undefined
                }
              />
            </Stack>
            <Box>
              {dossierTermination.data.driverNotifiedOfTermination === false &&
                authorisedToTerminate && (
                  <APButton
                    text="Employee Notified"
                    icon="play"
                    variant="contained"
                    color="secondary"
                    onClick={onDriverNotifiedOfTerminationClick}
                  />
                )}
            </Box>
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default EmployerViewDossierTermination;
